import { type INPMetric, onCLS, onFCP, onINP, onTTFB } from "web-vitals";
// IMPORTANT: `LCPMetricWithAttribution` is the compile-time guard for the
// upstream → wire field mapping in `buildLcpAttribution`. Do NOT downgrade this
// to the base `LCPMetric` type — if you do, a future rename in web-vitals
// (e.g. `target` → `selector`) will compile cleanly and silently emit `null`
// for `attribution.element` in production.
import { type LCPMetricWithAttribution, onLCP } from "web-vitals/attribution";
import { collectEnvironmentHints } from "./environmentHints";
import { generateUUID } from "./private/generateUuid";
import { sanitizeUrl } from "./private/sanitizeUrl";
import type {
  BridgeTiming,
  CutoffSource,
  EnvironmentHints,
  FirstLoadTimingMetrics,
  ImageSummary,
  ImmediateEventMetrics,
  ImmediateEventPayload,
  LazyEventDebugTelemetry,
  LazyEventMetricName,
  LazyEventMetrics,
  LazyEventPayload,
  LCPAttribution,
  LCPMetrics,
  RequestTiming,
  Transporter,
  WarpDeploymentInfo,
} from "./types";
import { collectWarpDeploymentInfo } from "./warpDeploymentInfo";

const IOS_FIRST_API_DEADLINE_MS = 1500;

// Waterfall cutoff for WebViews that never emit LCP (old iOS WKWebView lacks
// the LargestContentfulPaint API — measured 0% on iOS <= 18, 60% on iOS 26+):
// flushes fall back to this fixed cutoff, the web-vitals "good LCP" boundary.
// Doubles as the arming delay for the waterfall flush below — a flush at the
// same instant as the cutoff would miss bars for requests that started just
// inside the window and had not completed yet, so the two must not coincide;
// scheduling the flush *after* this window elapses is what separates them.
const LCP_FALLBACK_CUTOFF_MS = 2500;

// Upper bound on how long the waterfall flush may wait for an idle period.
// requestIdleCallback can be starved indefinitely on a busy page — which is
// exactly the page we are trying to rescue — so cap the wait rather than
// introduce a second tuning knob.
const WATERFALL_FLUSH_IDLE_TIMEOUT_MS = LCP_FALLBACK_CUTOFF_MS;

// Waterfall fields are NOT latched through `dispatchedFields`. They are
// tracked by count in `sentWaterfallCounts` so a later flush can ship an
// updated array — see the high-water mark in dispatchLazyDelta.
const WATERFALL_FIELDS: ReadonlySet<keyof LazyEventMetrics> = new Set([
  "api_request_timings",
  "resource_timings",
  "image_summary",
]);

// Byte backstop for the bridge message: WKScriptMessageHandler runs on the
// app's main thread, so an oversized analytics payload degrades the APP, not
// just the page. 8KB on the metrics delta leaves headroom for the event
// envelope under the ~10KB bridge guideline. Bars are sorted by start_time,
// so popping from the end drops the latest-starting bars first — the same
// semantics as the per-field count cap. image_summary is ~130B fixed and is
// never trimmed.
const WATERFALL_BYTE_BUDGET = 8192;

// Pops in place, so the arrays handed in must not be shared with anything that
// outlives this call. Safe because the collectors return a fresh array per call
// (collectTimings is a .filter().map() chain) — and that matters more now that
// a page can flush the waterfall more than once.
function truncateWaterfallToBudget(delta: Partial<LazyEventMetrics>): void {
  // ponytail: string length ≈ UTF-16 code units, not UTF-8 bytes — close
  // enough for a backstop over ASCII-dominated payloads.
  let bytes = JSON.stringify(delta).length;
  for (const field of ["resource_timings", "api_request_timings"] as const) {
    if (bytes <= WATERFALL_BYTE_BUDGET) return;
    const rows = delta[field];
    if (rows === undefined) continue;
    while (rows.length > 0 && bytes > WATERFALL_BYTE_BUDGET) {
      const dropped = rows.pop() as RequestTiming;
      bytes -= JSON.stringify(dropped).length + 1; // +1: the removed comma
    }
    if (rows.length === 0) delete delta[field];
  }
}

// Flip to true and rebuild to attach _debug telemetry to LazyEvent payloads.
// Kept as a module-level const so tsup/esbuild dead-code-eliminates the branch
// in production builds. No env-var indirection — xview-perf has no Node types
// and runs in a WebView, so a manual toggle is the right granularity here.
const DEBUG_TELEMETRY = false;

function isIOSWebView(): boolean {
  return navigator.userAgent.includes("iOS");
}

// Upstream → wire field mapping for LCP attribution.
// `web-vitals/attribution` emits the LCP element CSS selector under `target`,
// but BQ analysts read this column as `attribution.element`. We isolate the
// rename to this single boundary so call sites and downstream queries see the
// wire-shaped name only. Type safety on `upstream.target` comes from
// `LCPMetricWithAttribution` at the import site above — keep them in sync.
// Exported for integration testing against real web-vitals types.
export function buildLcpAttribution(
  upstream: LCPMetricWithAttribution["attribution"] | undefined,
): LCPAttribution | undefined {
  if (!upstream) return undefined;
  return {
    element: upstream.target ?? null,
    resource_url: sanitizeResourceUrl(upstream.url ?? ""),
    time_to_first_byte: upstream.timeToFirstByte,
    resource_load_delay: upstream.resourceLoadDelay,
    resource_load_duration: upstream.resourceLoadDuration,
    element_render_delay: upstream.elementRenderDelay,
  };
}

function scheduleIdleCallback(callback: () => void, timeoutMs?: number): void {
  if ("requestIdleCallback" in window) {
    requestIdleCallback(
      () => callback(),
      timeoutMs === undefined ? undefined : { timeout: timeoutMs },
    );
  } else {
    setTimeout(() => callback(), 0);
  }
}

// data: payloads are redacted in place (they have no origin/path to keep);
// everything else is an image/font URL, so it takes the asset allowlist —
// the CDN transform params (size/quality/format) are what explain a long
// resource_load_duration, while `id`/`Signature` are dropped with the rest.
function sanitizeResourceUrl(resourceUrl: string): string {
  return resourceUrl.startsWith("data:")
    ? redactDataUrl(resourceUrl)
    : sanitizeUrl(resourceUrl, "asset");
}

function redactDataUrl(resourceUrl: string): string {
  if (!resourceUrl.startsWith("data:")) return resourceUrl;

  const metadataEndIndex = resourceUrl.indexOf(",");
  const metadata =
    metadataEndIndex === -1
      ? resourceUrl.slice("data:".length)
      : resourceUrl.slice("data:".length, metadataEndIndex);
  const metadataParts = metadata.split(";");
  const mediaType = metadataParts[0] ?? "";
  const hasBase64 = metadataParts.some(
    (part) => part.toLowerCase() === "base64",
  );
  const redactedUrl = `data:${mediaType};x-xview-perf=redacted;x-original-chars=${resourceUrl.length}`;

  return hasBase64 ? `${redactedUrl};base64` : redactedUrl;
}

const FIRST_LOAD_TIMING_KEYS = [
  "first_api_request",
  "first_script_resource",
  "entry_script_resource",
  "app_main_script_resource",
  "excluded_instrumentation_script_urls",
] as const satisfies ReadonlyArray<keyof FirstLoadTimingMetrics>;

export class PerformanceTracker {
  private isInitialized = false;
  private entryUrl = "";
  private readonly location: Location;
  private readonly transporter: Transporter;
  private readonly traceId: string;
  // Page-constant cohort covariates, attached to every payload (see types.ts).
  private readonly environment: EnvironmentHints | undefined;
  private readonly deployment: WarpDeploymentInfo | undefined;

  private readonly lazyEventMetricNames: LazyEventMetricName[] = isIOSWebView()
    ? ["fcp", "ttfb"]
    : ["fcp", "ttfb", "cls"];
  private collectedLazyEventMetrics: Partial<LazyEventMetrics> = {};
  private isIdleDispatchScheduled = false;
  private dispatchedFields = new Set<keyof LazyEventMetrics>();
  private iosFirstApiDeadlineTimerId: ReturnType<typeof setTimeout> | null =
    null;
  private dispatchCount = 0;
  private gateSatisfiedAtMs: number | undefined = undefined;
  // Stored in analyze() so notifyFirstApiReady() can call dispatchLazyDelta directly.
  private storedOnResult:
    | ((measurement: ImmediateEventPayload | LazyEventPayload) => void)
    | null = null;
  private storedGetFirstLoadTiming:
    | (() => FirstLoadTimingMetrics | undefined)
    | undefined = undefined;
  private storedGetApiRequestTimings:
    | ((cutoffTime: number) => RequestTiming[])
    | undefined = undefined;
  private storedGetResourceTimings:
    | ((cutoffTime: number) => RequestTiming[])
    | undefined = undefined;
  private storedGetImageSummary:
    | ((cutoffTime: number) => ImageSummary | undefined)
    | undefined = undefined;
  private storedGetBridgeState: (() => BridgeTiming) | undefined = undefined;
  // LCP render time, kept current by the reportAllChanges:true subscription in
  // analyze(). Used as the cutoff for the api_request_timings waterfall, read
  // by dispatchLazyDelta at flush time.
  private lcpValue: number | undefined = undefined;
  // Bars already shipped per waterfall field (image_summary uses its `count`),
  // counted BEFORE the 8KB backstop trims them — recording the post-trim length
  // would make every later flush re-ship the same truncated array forever.
  private sentWaterfallCounts = new Map<keyof LazyEventMetrics, number>();
  private sentCutoffSource: CutoffSource | undefined = undefined;

  constructor({
    location,
    transporter,
  }: {
    location: Location;
    transporter: Transporter;
  }) {
    this.location = location;
    this.transporter = transporter;
    this.entryUrl = sanitizeUrl(location.href, "page");
    this.traceId = generateUUID();
    this.environment = collectEnvironmentHints();
    this.deployment = collectWarpDeploymentInfo();
  }

  // Single sanitize boundary for every location.href-derived field
  // (entry_url, captured_url, and the per-metric urls). Recomputed per read
  // rather than cached: an SPA route change between LCP and the terminal
  // flush is real signal, and collapsing it would hide it.
  private currentUrl(): string {
    return sanitizeUrl(this.location.href, "page");
  }

  private commonEventFields() {
    return {
      urls: {
        entry_url: this.entryUrl,
        captured_url: this.currentUrl(),
      },
      captured_at: new Date().toISOString(),
      request_id: generateUUID(),
      trace_id: this.traceId,
      ...(this.deployment === undefined ? {} : { deployment: this.deployment }),
    };
  }

  private wrapImmediate(metrics: ImmediateEventMetrics): ImmediateEventPayload {
    const payload: ImmediateEventPayload = {
      type: "ImmediateEvent",
      metrics,
      ...this.commonEventFields(),
    };
    if (this.environment !== undefined) payload.environment = this.environment;
    return payload;
  }

  private wrapLazy(
    metrics: Partial<LazyEventMetrics>,
    debug?: LazyEventDebugTelemetry,
  ): LazyEventPayload {
    const payload: LazyEventPayload = {
      type: "LazyEvent",
      metrics,
      ...this.commonEventFields(),
    };
    if (this.environment !== undefined) payload.environment = this.environment;
    if (debug !== undefined) payload._debug = debug;
    return payload;
  }

  private buildLcpMetric(
    value: number,
    attribution: LCPAttribution | undefined,
  ): LCPMetrics {
    const metric: LCPMetrics = { value, url: this.currentUrl() };
    if (attribution !== undefined) metric.attribution = attribution;
    return metric;
  }

  private isAllLazyEventMetricsCollected(): boolean {
    return this.lazyEventMetricNames.every(
      (name) => this.collectedLazyEventMetrics[name] !== undefined,
    );
  }

  private send(measurement: ImmediateEventPayload | LazyEventPayload): void {
    this.transporter.send({
      ...measurement,
      sent_at: new Date().toISOString(),
    });
  }

  private dispatchLazyDelta(
    onResult: (measurement: ImmediateEventPayload | LazyEventPayload) => void,
    getFirstLoadTiming: (() => FirstLoadTimingMetrics | undefined) | undefined,
    trigger:
      | "idle"
      | "deadline"
      | "hidden"
      | "pagehide"
      | "first_api_ready"
      | "waterfall",
  ): void {
    const delta: Partial<LazyEventMetrics> = {};

    for (const [key, value] of Object.entries(this.collectedLazyEventMetrics)) {
      const fieldKey = key as keyof LazyEventMetrics;
      if (value === undefined) continue;
      if (this.dispatchedFields.has(fieldKey)) continue;
      Object.assign(delta, { [fieldKey]: value });
    }

    const firstLoad = getFirstLoadTiming?.();
    if (firstLoad !== undefined) {
      for (const fieldKey of FIRST_LOAD_TIMING_KEYS) {
        const value = firstLoad[fieldKey];
        if (value === undefined) continue;
        if (this.dispatchedFields.has(fieldKey)) continue;
        Object.assign(delta, { [fieldKey]: value });
      }
    }

    // Request waterfalls (API + static bars + image aggregate), cut off at the
    // LCP render time. WebViews that never emit LCP (old iOS) fall back to
    // LCP_FALLBACK_CUTOFF_MS; the shared cutoff_source keeps the two
    // populations separable downstream. Each field is only added when
    // non-empty so it retries on the next flush instead of latching empty.
    const waterfallSources = [
      ["api_request_timings", this.storedGetApiRequestTimings],
      ["resource_timings", this.storedGetResourceTimings],
    ] as const;
    const isTerminalFlush = trigger === "hidden" || trigger === "pagehide";
    // The fallback cutoff used to be terminal-flush-only, on the assumption
    // that hidden/pagehide is the last chance to ship. In a native WebView the
    // host app routinely destroys the page without firing either, so that
    // flush often never comes at all (measured: ~37% of Android loads emit a
    // hidden event). Allow the fallback on any flush that happens after the
    // fallback window itself has already elapsed — by then the cutoff is as
    // meaningful as it would be at teardown.
    const canUseFallbackCutoff =
      isTerminalFlush || performance.now() >= LCP_FALLBACK_CUTOFF_MS;
    const cutoffTime =
      this.lcpValue ??
      (canUseFallbackCutoff ? LCP_FALLBACK_CUTOFF_MS : undefined);
    // Bar counts as collected this flush, recorded only if the field survives
    // the backstop below (a field the backstop drops must stay retryable).
    const collectedCounts = new Map<keyof LazyEventMetrics, number>();
    if (cutoffTime !== undefined) {
      const nextCutoffSource: CutoffSource =
        this.lcpValue !== undefined ? "lcp" : "deadline";
      // A waterfall already shipped against the fixed fallback is mislabelled
      // once the real LCP arrives — even when the bar count is unchanged — so
      // treat that upgrade as an improvement and re-ship with the right label.
      const cutoffSourceImproved =
        this.sentCutoffSource === "deadline" && nextCutoffSource === "lcp";

      for (const [fieldKey, getTimings] of waterfallSources) {
        if (getTimings === undefined) continue;
        const timings = getTimings(cutoffTime);
        if (timings.length === 0) continue;
        // Count high-water mark rather than a boolean latch: a request that
        // started inside the window but finished after the flush only enters
        // the accumulator later, and a late LCP widens the window. Re-ship the
        // WHOLE array (not just the tail) so a dropped bridge message
        // self-heals — downstream takes the longest array per trace_id.
        if (
          !cutoffSourceImproved &&
          timings.length <= (this.sentWaterfallCounts.get(fieldKey) ?? 0)
        ) {
          continue;
        }
        Object.assign(delta, { [fieldKey]: timings });
        collectedCounts.set(fieldKey, timings.length);
      }
      if (this.storedGetImageSummary !== undefined) {
        const imageSummary = this.storedGetImageSummary(cutoffTime);
        if (
          imageSummary !== undefined &&
          (cutoffSourceImproved ||
            imageSummary.count >
              (this.sentWaterfallCounts.get("image_summary") ?? 0))
        ) {
          delta.image_summary = imageSummary;
          collectedCounts.set("image_summary", imageSummary.count);
        }
      }
      // A field emptied by the backstop leaves delta, so it retries on the
      // next flush — when the other array is already dispatched and the
      // budget is freer.
      truncateWaterfallToBudget(delta);
      const shippedWaterfall =
        delta.api_request_timings !== undefined ||
        delta.resource_timings !== undefined ||
        delta.image_summary !== undefined;
      // Re-sent on every flush that ships a waterfall field, not just the
      // first: the arrays can split across flushes (backstop-emptied or
      // late-filled), and a waterfall row without cutoff_source can't be
      // assigned to the lcp/deadline population downstream. ~30 bytes.
      if (shippedWaterfall) {
        delta.cutoff_source = nextCutoffSource;
      }
    }

    if (Object.keys(delta).length === 0) return;

    // KarrotBridge readiness, sampled at the instant this flush ships (see
    // BridgeTiming in types.ts). Added only once the delta is known to be
    // non-empty: it rides along with whatever is already going out and is
    // never worth a bridge message of its own.
    //
    // Deliberately NOT routed through `collectedLazyEventMetrics` /
    // `dispatchedFields`. Latching it would freeze the first sample and leave
    // exactly ONE observation per trace, which is the entire measurement —
    // the same trap `cutoff_source` avoids above. ~120 bytes per flush.
    const bridgeTiming = this.storedGetBridgeState?.();
    if (bridgeTiming !== undefined) {
      delta.bridge_timing = bridgeTiming;
    }

    const dispatchedKeys = Object.keys(delta) as (keyof LazyEventMetrics)[];

    this.dispatchCount += 1;

    let debug: LazyEventDebugTelemetry | undefined;
    if (DEBUG_TELEMETRY) {
      debug = {
        dispatch_trigger: trigger,
        dispatch_count: this.dispatchCount,
        has_first_api_request_at_dispatch:
          firstLoad?.first_api_request !== undefined,
        gate_satisfied_at_ms: this.gateSatisfiedAtMs ?? 0,
      };
    }

    onResult(this.wrapLazy(delta, debug));
    for (const key of dispatchedKeys) {
      // Waterfall fields are deliberately not latched — they advance the
      // count high-water mark below so a fuller array can still be shipped.
      if (WATERFALL_FIELDS.has(key)) continue;
      // Neither is bridge_timing: it is re-sampled every flush by design.
      if (key === "bridge_timing") continue;
      this.dispatchedFields.add(key);
    }
    for (const [fieldKey, count] of collectedCounts) {
      if (delta[fieldKey] === undefined) continue; // backstop dropped it
      this.sentWaterfallCounts.set(fieldKey, count);
    }
    if (delta.cutoff_source !== undefined) {
      this.sentCutoffSource = delta.cutoff_source;
    }
  }

  private installHiddenHandler(
    onResult: (measurement: ImmediateEventPayload | LazyEventPayload) => void,
    getFirstLoadTiming?: () => FirstLoadTimingMetrics | undefined,
  ): void {
    if (typeof document === "undefined") return;
    // Deliberately NOT skipped when the document starts hidden. Native
    // WebViews are commonly pre-warmed: the head IIFE evaluates while the page
    // is still hidden, and it becomes visible later (see the same note in
    // firstApiRequestHook.ts). Bailing here permanently cost those pages their
    // only terminal flush.

    let triggered = false;
    const flush = (trigger: "hidden" | "pagehide"): void => {
      if (triggered) return;
      triggered = true;
      if (this.iosFirstApiDeadlineTimerId !== null) {
        clearTimeout(this.iosFirstApiDeadlineTimerId);
        this.iosFirstApiDeadlineTimerId = null;
      }
      this.dispatchLazyDelta(onResult, getFirstLoadTiming, trigger);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
    };

    const onVisibilityChange = (): void => {
      if (document.visibilityState !== "hidden") return;
      flush("hidden");
    };
    const onPageHide = (): void => {
      flush("pagehide");
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", onPageHide);
    }
  }

  private isAllDeferredFieldsPresent(): boolean {
    const timing = this.storedGetFirstLoadTiming?.();
    return (
      timing?.first_api_request !== undefined &&
      timing?.entry_script_resource !== undefined &&
      timing?.app_main_script_resource !== undefined
    );
  }

  private hasUndispatchedFirstApiRequest(): boolean {
    const timing = this.storedGetFirstLoadTiming?.();
    return (
      timing?.first_api_request !== undefined &&
      !this.dispatchedFields.has("first_api_request")
    );
  }

  private dispatchStoredLazyDelta(
    trigger: "deadline" | "first_api_ready",
  ): void {
    if (this.storedOnResult === null) return;
    this.dispatchLazyDelta(
      this.storedOnResult,
      this.storedGetFirstLoadTiming,
      trigger,
    );
  }

  // Called by the IIFE's first-API hooks. While the iOS deadline is active, this
  // keeps the grouped first-load gate; after the deadline, it ships a late
  // first_api_request delta without waiting for a lifecycle flush.
  notifyFirstApiReady(): void {
    if (this.iosFirstApiDeadlineTimerId !== null) {
      if (!this.isAllDeferredFieldsPresent()) return;
      clearTimeout(this.iosFirstApiDeadlineTimerId);
      this.iosFirstApiDeadlineTimerId = null;
      this.dispatchStoredLazyDelta("deadline");
      return;
    }

    if (this.hasUndispatchedFirstApiRequest()) {
      this.dispatchStoredLazyDelta("first_api_ready");
    }
  }

  // Called by the IIFE's observeIndexScript onLoad hook — promotes deferred
  // iOS dispatch to fire immediately once all three deferred fields are captured.
  notifyEntryScriptReady(): void {
    if (this.iosFirstApiDeadlineTimerId !== null) {
      if (!this.isAllDeferredFieldsPresent()) return;
      clearTimeout(this.iosFirstApiDeadlineTimerId);
      this.iosFirstApiDeadlineTimerId = null;
      this.dispatchStoredLazyDelta("deadline");
    }
  }

  // Called by the IIFE's observeAppScript onLoad hook — promotes deferred
  // iOS dispatch to fire immediately once all three deferred fields are captured.
  notifyBundleScriptReady(): void {
    if (this.iosFirstApiDeadlineTimerId !== null) {
      if (!this.isAllDeferredFieldsPresent()) return;
      clearTimeout(this.iosFirstApiDeadlineTimerId);
      this.iosFirstApiDeadlineTimerId = null;
      this.dispatchStoredLazyDelta("deadline");
    }
  }

  private analyze(
    onResult: (measurement: ImmediateEventPayload | LazyEventPayload) => void,
    getFirstLoadTiming?: () => FirstLoadTimingMetrics | undefined,
    getApiRequestTimings?: (cutoffTime: number) => RequestTiming[],
    getResourceTimings?: (cutoffTime: number) => RequestTiming[],
    getImageSummary?: (cutoffTime: number) => ImageSummary | undefined,
    getBridgeState?: () => BridgeTiming,
  ): void {
    if (this.isInitialized) return;
    this.isInitialized = true;
    this.storedOnResult = onResult;
    this.storedGetFirstLoadTiming = getFirstLoadTiming;
    this.storedGetApiRequestTimings = getApiRequestTimings;
    this.storedGetResourceTimings = getResourceTimings;
    this.storedGetImageSummary = getImageSummary;
    this.storedGetBridgeState = getBridgeState;

    onLCP(
      (metric: LCPMetricWithAttribution) => {
        if (
          metric.value == null ||
          !Number.isFinite(metric.value) ||
          metric.value < 0
        ) {
          console.warn(
            "[xview-perf] Invalid LCP value, skipping report",
            metric,
          );
          return;
        }
        const attribution = buildLcpAttribution(metric.attribution);
        onResult(
          this.wrapImmediate({
            lcp: this.buildLcpMetric(metric.value, attribution),
          }),
        );
      },
      { reportAllChanges: false },
    );

    // Second subscription, for the waterfall cutoff only — it never emits an
    // event, so the LCP metric on the wire is unchanged by it.
    //
    // The subscription above reports once, and only when web-vitals finalizes
    // LCP: keydown, click, or visibilitychange (see onLCP's `stopListening` —
    // notably NOT pagehide). A WebView often gets none of those before the app
    // destroys it, which is why LCP only reaches ~50% of loads in production.
    // With reportAllChanges:true, bindReporter calls back on every LCP entry
    // as it arrives, so the cutoff is known from the first contentful paint
    // instead of from teardown. web-vitals still owns correctness here
    // (prerender activationStart, firstHiddenTime guard, bfcache re-init) —
    // that is exactly why this is a second onLCP rather than a hand-rolled
    // PerformanceObserver.
    onLCP(
      (metric: LCPMetricWithAttribution) => {
        if (
          metric.value == null ||
          !Number.isFinite(metric.value) ||
          metric.value < 0
        ) {
          return;
        }
        this.lcpValue = metric.value;
      },
      { reportAllChanges: true },
    );

    const collectLazyEventMetric =
      (name: LazyEventMetricName) => (metric: { value: number }) => {
        if (this.dispatchedFields.has(name)) return;
        this.collectedLazyEventMetrics[name] = {
          value: metric.value,
          url: this.currentUrl(),
        };

        if (
          this.isAllLazyEventMetricsCollected() &&
          !this.isIdleDispatchScheduled
        ) {
          this.isIdleDispatchScheduled = true;
          if (this.gateSatisfiedAtMs === undefined) {
            this.gateSatisfiedAtMs = performance.now();
          }

          // iOS-only: defer dispatch until first_api_request + entry_script_resource +
          // app_main_script_resource all arrive or deadline expires. On iOS the gate is
          // [fcp, ttfb] (no CLS), and rIC falls back to setTimeout(0), so
          // dispatch consistently races ahead of the named-script 500ms debounce.
          const firstLoadAtUnlock = getFirstLoadTiming?.();
          const needsDeferral =
            isIOSWebView() &&
            (firstLoadAtUnlock?.first_api_request === undefined ||
              firstLoadAtUnlock?.entry_script_resource === undefined ||
              firstLoadAtUnlock?.app_main_script_resource === undefined);

          if (needsDeferral) {
            this.iosFirstApiDeadlineTimerId = setTimeout(() => {
              this.iosFirstApiDeadlineTimerId = null;
              this.dispatchLazyDelta(onResult, getFirstLoadTiming, "deadline");
            }, IOS_FIRST_API_DEADLINE_MS);
          } else {
            scheduleIdleCallback(() => {
              this.dispatchLazyDelta(onResult, getFirstLoadTiming, "idle");
            });
          }
        }
      };

    onFCP(collectLazyEventMetric("fcp"), { reportAllChanges: false });
    onCLS(collectLazyEventMetric("cls"), { reportAllChanges: false });
    onTTFB(collectLazyEventMetric("ttfb"), { reportAllChanges: false });
    onINP(
      (metric: INPMetric) => {
        onResult(
          this.wrapImmediate({
            inp: { value: metric.value, url: this.currentUrl() },
          }),
        );
      },
      { reportAllChanges: false },
    );

    // Register hidden/pagehide handler LAST so web-vitals' final synchronous
    // LCP/CLS callbacks (registered above) run before our flush ships the lazy
    // delta. Listener execution order = registration
    // order in the DOM event model.
    this.installHiddenHandler(onResult, getFirstLoadTiming);

    // The only flush that could ever carry a waterfall was the terminal one,
    // and on Android the sole other flush (`first_api_ready`) fires before LCP
    // is known — so a page the app tears down silently shipped no waterfall at
    // all. Measured: 64.5% of Android traces sent exactly one LazyEvent, which
    // by construction could not carry one.
    //
    // Arm a single flush once the fallback window has elapsed, so a waterfall
    // ships regardless of whether the app ever signals teardown. It costs at
    // most ONE extra bridge message per page load: dispatchLazyDelta returns
    // without calling the transporter when nothing new is pending, and the
    // waterfall fields ship at most once more via their count high-water mark.
    //
    // The handle is deliberately NOT kept and cleared on the terminal flush the
    // way iosFirstApiDeadlineTimerId is: `hidden` is not terminal here. A
    // pre-warmed WebView can go hidden and come back — the same case the
    // handler above stopped bailing on — so clearing the timer there would cost
    // those pages the very flush this arms. Firing after a terminal flush is
    // harmless: dispatchLazyDelta returns without calling the transporter when
    // nothing new is pending.
    setTimeout(() => {
      scheduleIdleCallback(() => {
        this.dispatchLazyDelta(onResult, getFirstLoadTiming, "waterfall");
      }, WATERFALL_FLUSH_IDLE_TIMEOUT_MS);
    }, LCP_FALLBACK_CUTOFF_MS);
  }

  report(options?: {
    getFirstLoadTiming?: () => FirstLoadTimingMetrics | undefined;
    getApiRequestTimings?: (cutoffTime: number) => RequestTiming[];
    getResourceTimings?: (cutoffTime: number) => RequestTiming[];
    getImageSummary?: (cutoffTime: number) => ImageSummary | undefined;
    getBridgeState?: () => BridgeTiming;
  }): void {
    this.analyze(
      (measurement) => {
        this.send(measurement);
      },
      options?.getFirstLoadTiming,
      options?.getApiRequestTimings,
      options?.getResourceTimings,
      options?.getImageSummary,
      options?.getBridgeState,
    );
  }
}
